// F9.2-X · Frontend Data Governance — State Access Layer (SAL).
// Camada ÚNICA e OBRIGATÓRIA para acesso a dados de EVM / KPIs / flags de motor.
//
// Invariantes (mem://architecture/ubs-eap-design-invariants):
//   1. Fonte única e travada de KPIs = emp_vw_indicadores_blindados.
//   2. Flags de motor (emp_motor_flags.epc_qtd_enabled etc.) NÃO são consultadas
//      pela UI — qualquer reativação exige PR de rollback explícito.
//   3. Hooks de KPI NÃO importam o supabase client diretamente; consomem somente
//      as funções exportadas deste módulo.
//
// Enforcement:
//   - Lint rule NO_DIRECT_EVM_ACCESS bloqueia `from('emp_motor_flags' | 'emp_vw_evm_*' | ...)`
//     fora de src/lib/evm/dataAccess.ts.
//   - Attack test (tests/frontend/evm_bypass.attack.test.ts) garante build fail
//     se qualquer hook tentar bypass.

import { supabase } from '@/integrations/supabase/client';
import { EVM_KPI_SOURCE } from './source';
import { assertEvmIntegrity } from './integrity';

/* ------------------------------------------------------------------ */
/* Tipos públicos                                                     */
/* ------------------------------------------------------------------ */

export interface EvmFlags {
  /** F9.2-X · F1: travado em false. Não consulta backend. */
  epcQtdEnabled: false;
  /** Identificador da fonte travada (debug/observabilidade). */
  source: typeof EVM_KPI_SOURCE.view;
}

export interface ContractKpiRow {
  contrato_id: string;
  periodo_referencia: string;
  pv: number;
  ev: number;
  ac: number;
  spi: number | null;
  cpi: number | null;
  eac: number | null;
  etc: number | null;
  vac: number | null;
  baseline_bac: number | null;
  percentual_fisico_previsto: number;
  percentual_fisico_realizado: number;
  percentual_financeiro_previsto: number;
  percentual_financeiro_realizado: number;
  /** Source identifier para debug — sempre 'blindados' após F9.2-X/F1. */
  _evm_source: 'blindados';
  // Campos opcionais que vêm da view blindada
  [extra: string]: unknown;
}

/* ------------------------------------------------------------------ */
/* API pública                                                         */
/* ------------------------------------------------------------------ */

/**
 * Retorna o estado das flags de motor EVM.
 *
 * F9.2-X · F1 (locked): sempre `epcQtdEnabled = false`. Não consulta backend.
 * Mantido como função para compatibilidade futura caso novas flags sejam
 * introduzidas via PR controlado.
 */
export function getEvmFlags(): EvmFlags {
  // Object.freeze garante imutabilidade observável em runtime — qualquer
  // tentativa de tamper (`flags.epcQtdEnabled = true`) lança em strict mode
  // (módulos ESM são strict por padrão).
  return Object.freeze({
    epcQtdEnabled: false,
    source: EVM_KPI_SOURCE.view,
  }) as EvmFlags;
}

/**
 * Retorna a fonte oficial de KPIs (sempre `emp_vw_indicadores_blindados`).
 * Use este getter ao logar / exibir badge "Origem dos indicadores".
 */
export function getEvmSource(): typeof EVM_KPI_SOURCE {
  return EVM_KPI_SOURCE;
}

/**
 * Busca a série de KPIs de EVM de um contrato a partir da view oficial e
 * travada (`emp_vw_indicadores_blindados`). É a ÚNICA forma autorizada de
 * obter PV/EV/AC/SPI/CPI/EAC/etc para a UI.
 *
 * Retorna sempre um array (vazio em caso de erro/sem dados).
 */
export async function getContractKpis(
  contratoId: string,
): Promise<ContractKpiRow[]> {
  if (!contratoId) return [];
  const { data, error } = await (supabase as any)
    .from(EVM_KPI_SOURCE.view)
    .select('*')
    .eq('contrato_id', contratoId)
    .order('periodo_referencia', { ascending: true });

  if (error) {
    // Não há fallback silencioso (F9.2-X · F1). Erro propaga para o caller.
    console.warn('[EVM-SAL] getContractKpis error:', error.message);
    throw error;
  }

  const rows = ((data || []) as any[]).map((d) => ({
    ...d,
    _evm_source: 'blindados' as const,
  })) as ContractKpiRow[];

  // System Truth Contract · State Sanity Assertion (fail-closed).
  for (const row of rows) {
    assertEvmIntegrity(row as unknown as Record<string, unknown>);
  }
  return rows;
}

/**
 * Versão portfolio-wide: busca KPIs de múltiplos contratos numa só query.
 * Usado pelo dashboard agregado (substitui o antigo useEvmPortfolioKpis).
 */
export async function getPortfolioKpis(
  contratoIds: string[],
): Promise<ContractKpiRow[]> {
  if (!contratoIds.length) return [];
  const { data, error } = await (supabase as any)
    .from(EVM_KPI_SOURCE.view)
    .select('*')
    .in('contrato_id', contratoIds);

  if (error) {
    console.warn('[EVM-SAL] getPortfolioKpis error:', error.message);
    throw error;
  }

  const rows = ((data || []) as any[]).map((d) => ({
    ...d,
    _evm_source: 'blindados' as const,
  })) as ContractKpiRow[];

  // System Truth Contract · State Sanity Assertion (fail-closed).
  for (const row of rows) {
    assertEvmIntegrity(row as unknown as Record<string, unknown>);
  }
  return rows;
}
